import { useDebounce } from "@react-hook/debounce";
import useResizeObserver from "@react-hook/resize-observer";
import React, { useEffect, useState } from "react";

interface IRenderProps {
   startIndex: number;
   endIndex: number;
   wrapperTranslateX: number;
   wrapperWidth: number;
   itemTranslateX: number;
}

export interface IModulePager extends ModulePagerProps {
   setPagerElement: React.Dispatch<React.SetStateAction<HTMLElement | null>>,
   next: () => void,
   previous: () => void,
   reset: () => void,
   hasNext: boolean,
   hasPrevious: boolean,
   displayControls: boolean,
   renderProps: IRenderProps
}
export interface ModulePagerProps {
   itemCount: number,
   itemWidth: number,
   gapWidth: number,
}

const RESET_TIMEOUT = 100;

const getRenderProps = (width: number, currentItem: number, itemCount: number, step: number, renderLeadingPage: boolean, renderTrailingPage: boolean, itemWidth: number, gapWidth: number): IRenderProps => {
   const startIndex = Math.max(0, currentItem - (renderLeadingPage ? step : 0));
   return {
      startIndex,
      endIndex: Math.min(itemCount, currentItem + step + (renderTrailingPage ? step : 1)),
      wrapperTranslateX: currentItem > 0
         ? -1 * currentItem * (itemWidth + gapWidth)
         : 0,
      itemTranslateX: startIndex * (itemWidth + gapWidth),
      wrapperWidth: (itemWidth + gapWidth) * itemCount - gapWidth
   };
}

function useModulePager(props: ModulePagerProps): IModulePager {
   const { itemCount, itemWidth, gapWidth } = props;
   const [currentItem, setCurrentItem] = useState<number>(0);
   const [pagerElement, setPagerElement] = useState<HTMLElement | null>(null);

   const [width, setWidth] = useDebounce<number>(0, RESET_TIMEOUT);
   const [step, setStep] = useDebounce<number>(1, RESET_TIMEOUT);

   const [renderProps, setRenderProps] = useState<IRenderProps>({
      startIndex: 0,
      endIndex: itemCount,
      wrapperTranslateX: 0,
      itemTranslateX: 0,
      wrapperWidth: 0
   });

   useResizeObserver(pagerElement, entry => {
      setWidth(entry.contentRect.width);
      let newStep = Math.floor(entry.contentRect.width / itemWidth);
      if (newStep > 1) {
         const widthWithGaps = newStep * itemWidth + (gapWidth * (newStep - 1));
         if (widthWithGaps > entry.contentRect.width) {
            newStep--;
         }
      }
      setStep(newStep);
   });

   // const { ref, inView, entry } = useInView();
   // useEffect(() => {
   //    ref.
   // }, [pagerElement])

   useEffect(() => {
      setRenderProps(getRenderProps(width, currentItem, itemCount, step, false, false, itemWidth, gapWidth));
   }, [width, currentItem, itemCount, step, itemWidth, gapWidth]);

   const modulePager: IModulePager = {
      ...props,
      setPagerElement,
      // transform,
      next: async () => {
         if (currentItem < itemCount - step) {
            setRenderProps(getRenderProps(width, currentItem + step, itemCount, step, true, false, itemWidth, gapWidth));

            await new Promise(x => setTimeout(x, 500));

            setCurrentItem(currentItem + step);
         }
      },
      previous: async () => {
         if (currentItem >= step) {
            setRenderProps(getRenderProps(width, currentItem - step, itemCount, step, false, true, itemWidth, gapWidth));

            await new Promise(x => setTimeout(x, 500));

            setCurrentItem(currentItem - step);
         }
      },
      reset: () => {
         setCurrentItem(0);
         setRenderProps(getRenderProps(width, 0, itemCount, step, false, false, itemWidth, gapWidth));
      },
      hasNext: currentItem < itemCount - step,
      hasPrevious: currentItem >= step,
      displayControls: itemCount > step,

      renderProps: renderProps
   };

   return modulePager;
}

export default useModulePager;